import useFetchItem from '../useFetchItem.js';
import useMutateItem from '../useMutateItem.js';
import HttpService from '../httpServices';
import { routes } from '../apiroutes';
import { ErrorHandler } from '../errorHandler';

const httpService = new HttpService();

/**
 * Admin Overtime Service
 * Handles all admin overtime management API calls
 */

// Overtime Settings Management
export const useOvertimeSettings = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['adminOvertimeSettings'],
    queryFn: () => httpService.getData(routes.getAdminOvertimeSettings()),
    enabled: options.enabled !== false,
    retry: 2,
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 15 * 60 * 1000, // 15 minutes
  });

  return {
    settingsData: data?.data || data || {},
    settingsError: ErrorHandler(error),
    settingsIsLoading: isLoading,
    refetchSettings: refetch,
  };
};

export const useUpdateOvertimeSettings = (onSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) => httpService.putData(payload, routes.updateAdminOvertimeSettings()),
    onSuccess: (response) => {
      const resData = response?.data || {};
      if (onSuccess) onSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      console.error('Update overtime settings error:', error);
    },
  });

  return {
    updateSettingsData: data,
    updateSettingsError: ErrorHandler(error),
    updateSettingsIsLoading: isPending,
    updateSettings: mutate,
  };
};

// Overtime Requests Management
export const useOvertimeRequests = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['adminOvertimeRequests', options.filters],
    queryFn: () => {
      const url = routes.getAdminOvertimeRequests(options.filters || {});
      return httpService.getData(url);
    },
    enabled: options.enabled !== false,
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });



  return {
    requestsData: data?.data || data || [],
    requestsError: ErrorHandler(error),
    requestsIsLoading: isLoading,
    refetchRequests: refetch,
  };
};

export const useApproveOvertimeRequest = (onSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: ({ requestId, payload }) => 
      httpService.putData(payload, routes.approveAdminOvertimeRequest(requestId)),
    onSuccess: (response) => {
      const resData = response?.data || {};
      if (onSuccess) onSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      console.error('Approve overtime request error:', error);
    },
  });

  return {
    approveRequestData: data,
    approveRequestError: ErrorHandler(error),
    approveRequestIsLoading: isPending,
    approveRequest: mutate,
  };
};

// Manual Overtime Assignment
export const useAssignOvertime = (onSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: ({ employeeId, payload }) => 
      httpService.postData(payload, routes.assignAdminOvertime(employeeId)),
    onSuccess: (response) => {
      const resData = response?.data || {};
      if (onSuccess) onSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      console.error('Assign overtime error:', error);
    },
  });

  return {
    assignOvertimeData: data,
    assignOvertimeError: ErrorHandler(error),
    assignOvertimeIsLoading: isPending,
    assignOvertime: mutate,
  };
};

// Overtime Analytics
export const useOvertimeAnalytics = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['adminOvertimeAnalytics'],
    queryFn: () => httpService.getData(routes.getAdminOvertimeAnalytics()),
    enabled: options.enabled !== false,
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    analyticsData: data?.data || data || {},
    analyticsError: ErrorHandler(error),
    analyticsIsLoading: isLoading,
    refetchAnalytics: refetch,
  };
};

// Weekly Overtime Reports
export const useWeeklyOvertimeReports = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['adminWeeklyOvertimeReports', options.filters],
    queryFn: () => {
      const url = routes.getAdminWeeklyOvertimeReports(options.filters || {});
      return httpService.getData(url);
    },
    enabled: options.enabled !== false,
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    weeklyReportsData: data?.data?.reports || data?.reports || [],
    weeklyReportsError: ErrorHandler(error),
    weeklyReportsIsLoading: isLoading,
    refetchWeeklyReports: refetch,
  };
};

// Combined Admin Overtime Management Hook
export const useAdminOvertimeManagement = (options = {}) => {
  // Load settings
  const settings = useOvertimeSettings({ 
    enabled: options.enabled,
    staleTime: 15 * 60 * 1000,
    cacheTime: 20 * 60 * 1000,
  });

  // Load requests
  const requests = useOvertimeRequests({ 
    enabled: options.enabled,
    filters: options.filters,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  // Load analytics
  const analytics = useOvertimeAnalytics({ 
    enabled: options.enabled,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  // Load weekly reports
  const weeklyReports = useWeeklyOvertimeReports({ 
    enabled: options.enabled,
    filters: options.filters,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  return {
    // Settings
    settingsData: settings.settingsData,
    settingsError: settings.settingsError,
    settingsIsLoading: settings.settingsIsLoading,
    refetchSettings: settings.refetchSettings,

    // Requests
    requestsData: requests.requestsData,
    requestsError: requests.requestsError,
    requestsIsLoading: requests.requestsIsLoading,
    refetchRequests: requests.refetchRequests,

    // Analytics
    analyticsData: analytics.analyticsData,
    analyticsError: analytics.analyticsError,
    analyticsIsLoading: analytics.analyticsIsLoading,
    refetchAnalytics: analytics.refetchAnalytics,

    // Weekly Reports
    weeklyReportsData: weeklyReports.weeklyReportsData,
    weeklyReportsError: weeklyReports.weeklyReportsError,
    weeklyReportsIsLoading: weeklyReports.weeklyReportsIsLoading,
    refetchWeeklyReports: weeklyReports.refetchWeeklyReports,

    // Combined states
    isLoading: settings.settingsIsLoading || requests.requestsIsLoading || analytics.analyticsIsLoading,
    hasError: settings.settingsError || requests.requestsError || analytics.analyticsError,

    // Refetch all
    refetchAll: () => {
      settings.refetchSettings();
      requests.refetchRequests();
      analytics.refetchAnalytics();
      weeklyReports.refetchWeeklyReports();
    },
  };
};

// Utility functions for overtime management
export const overtimeManagementUtils = {
  // Format overtime hours
  formatOvertimeHours: (hours) => {
    if (!hours || hours <= 0) return '0h';
    return `${hours.toFixed(2)}h`;
  },

  // Get request status color
  getRequestStatusColor: (status) => {
    const colors = {
      'pending': 'yellow',
      'approved': 'green',
      'rejected': 'red',
      'cancelled': 'gray'
    };
    return colors[status] || 'gray';
  },

  // Get request status display name
  getRequestStatusDisplay: (status) => {
    const displays = {
      'pending': 'Pending',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'cancelled': 'Cancelled'
    };
    return displays[status] || status;
  },

  // Calculate total overtime hours from requests
  calculateTotalOvertime: (requests) => {
    return requests.reduce((total, request) => {
      if (request.status === 'approved') {
        return total + (request.hours_requested || 0);
      }
      return total;
    }, 0);
  },

  // Get pending requests count
  getPendingRequestsCount: (requests) => {
    return requests.filter(request => request.status === 'pending').length;
  },

  // Format currency for overtime pay
  formatOvertimePay: (hours, rate) => {
    if (!hours || !rate) return '$0.00';
    const pay = hours * rate;
    return `$${pay.toFixed(2)}`;
  }
}; 